import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router';
import { Alert, Box, CircularProgress, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from 'src/redux-app-hooks';
import { NotificationSeverity, showSnackbar } from 'src/features/notifications/notifications.slice';
import { isProblemDetailCode, isProblemDetailTextOr } from 'src/common/ProblemDetail';
import { admiRealEstateAssetService } from './admi-real-estate-asset.service';
import { RealEstateAsset } from './real-estate-asset-management.dto';
import { Building } from 'src/features/real-estate/buildings.dto';
import useGoogleMapsApiLoader from 'src/infrastructure/google/useGoogleMapsApiLoader';
import { ensanSteckbriefService } from './ensan-steckbrief.service';
import {
    CreateVariantRequest,
    HEATING_SYSTEM_OVERRIDE_KEY,
    MeasureKey,
    PRIORITIES_OVERRIDE_KEY,
    PreviewSteckbriefDocumentRequest,
    RecommendedHeatingSystemKey,
    SteckbriefAssumptionDefaultsDto,
    SteckbriefDto,
    SteckbriefVariantDto,
    VariantDocumentDto,
    VariantPrefillDto,
} from './ensan-steckbrief.dto';
import { reaPageUrl } from './rea-page-params';
import { stampPrefillFields } from './steckbrief-assumption-defaults';
import { assumptionMessage } from './steckbrief-calculation-messages';
import { SteckbriefVariantForm, VariantSaveResult } from './components/SteckbriefVariantForm';
import { SteckbriefBuildingMapPicker } from './components/SteckbriefBuildingMapPicker';
import { InlineSteckbriefPreview } from './components/SteckbriefPdfPreviewPanel';
import { PhotoTooLargeError, STECKBRIEF_PHOTO_MAX_BYTES, processCapturedPhoto } from './components/steckbrief-photo';

/**
 * Debounce for map-subset PATCHes (GEN-765): a burst of polygon toggles lands
 * as ONE `PATCH /steckbriefe/{id}` + ONE prefill re-resolution.
 */
const MAP_PATCH_DEBOUNCE_MS = 600;

/**
 * Open mode (GEN-765): the form prefill synthesized CLIENT-side from the
 * Variant's data stamp — `StampedField` and `PrefilledVariantField` share the
 * `{value, source}` shape, so the stamp's fields ARE the prefill, minus the
 * retired pre-GEN-802 window-share seed (stampPrefillFields): saving creates a
 * new Variant, which must not carry a value nobody entered.
 */
function prefillFromStamp(variant: SteckbriefVariantDto): VariantPrefillDto {
    return { kind: variant.kind, fields: stampPrefillFields(variant.dataStamp) };
}

/** Order-independent key of a building selection — compares a settled subset with the persisted one. */
function selectionKeyOf(ids: Iterable<string>): string {
    return [...ids].sort().join(',');
}

/**
 * The PATCH chain of the map subset: one PATCH at a time; the newest subset
 * settled while one runs is `queued`. `idle` resolves once the last one landed
 * (it never rejects). `gen` is the page context that started it.
 */
interface SelectionPatchChain {
    steckbriefId: string;
    gen: number;
    queued: Set<string> | null;
    idle: Promise<void>;
}

/**
 * A request this page aborted itself (unmount, context switch) — never a
 * failure to report. http-instance.ts rejects cancels without logging them.
 */
function isAborted(err: unknown): boolean {
    return axios.isCancel(err);
}

/** `", Germany"` suffix off a geocoded address — same trim as the REA page labels. */
function stripCountry(address: string): string {
    return address.replace(/, Germany$/, '');
}

/** Approximate center of a building for geocoding (outer-ring average, box fallback). */
function buildingCenter(b: Building): google.maps.LatLngLiteral | null {
    if (b.geometry) {
        const ring =
            b.geometry.type === 'Polygon'
                ? b.geometry.coordinates[0]
                : b.geometry.type === 'MultiPolygon'
                  ? b.geometry.coordinates[0]?.[0]
                  : null;
        if (ring && ring.length > 0) {
            const lngSum = ring.reduce((s, c) => s + c[0], 0);
            const latSum = ring.reduce((s, c) => s + c[1], 0);
            return { lat: latSum / ring.length, lng: lngSum / ring.length };
        }
    }
    if (b.box && b.box.length >= 4) {
        return { lat: (b.box[1] + b.box[3]) / 2, lng: (b.box[0] + b.box[2]) / 2 };
    }
    return null;
}

/**
 * The routed Steckbrief-Variant form page (RFC-004 §5, GEN-701; create+open
 * since GEN-765). Two routes share it:
 *
 * - `…/variants/new` — create: the Steckbrief is loaded AND the form is
 *   prefilled via `POST /variants/prefill` immediately (GEN-701 QA feedback:
 *   no pre-step page).
 * - `…/variants/:variantId` — open: the form is prefilled from that Variant's
 *   data stamp (client-side synthesis) incl. its stamped measures state.
 *   Saving still creates a NEW Variant — Variants stay immutable.
 *
 * On top of the form sits the building-subset map (GEN-765): toggling a
 * polygon PATCHes the Steckbrief's `buildingIds` (debounced) and re-resolves
 * the prefill, which the form merges (USER_PROVIDED edits win). NOTE: the
 * PATCH persists even when the user leaves without saving — the prefill
 * resolves SERVER-side from the persisted subset, so deferring it is not an
 * option. The map is also the Gebäudefoto camera (GEN-778): "Save map image"
 * photographs the framed map (selected buildings only), masks it to the print
 * format and hands it to the form's photo field. Document actions live in the
 * footer since GEN-765: the stateless
 * preview download (GEN-762), save, save-then-generate, and open-mode
 * generation from the saved stamp.
 */
export default function SteckbriefVariantFormPage() {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { reaId, steckbriefId, variantId } = useParams<{
        reaId: string;
        steckbriefId: string;
        variantId?: string;
    }>();
    const googleMapsLoader = useGoogleMapsApiLoader();

    const [steckbrief, setSteckbrief] = useState<SteckbriefDto | null>(null);
    const [rea, setRea] = useState<RealEstateAsset | null>(null);
    const [prefill, setPrefill] = useState<VariantPrefillDto | null>(null);
    const [prefillVersion, setPrefillVersion] = useState(0);
    const [openedVariant, setOpenedVariant] = useState<SteckbriefVariantDto | null>(null);
    // The model defaults per Gebäudetyp (GEN-802): undefined while loading,
    // null when the load failed (the form then seeds nothing).
    const [assumptionDefaults, setAssumptionDefaults] = useState<SteckbriefAssumptionDefaultsDto | null | undefined>(
        undefined,
    );
    const [activeVariantNames, setActiveVariantNames] = useState<string[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [preview, setPreview] = useState<InlineSteckbriefPreview | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    // Only the newest preview render may apply its response (renders overlap
    // when calculations follow each other quickly).
    const previewSeqRef = useRef(0);
    // The payload of the newest preview render, in flight or shown; null after
    // a failed render. An identical request is skipped — see handlePreview.
    const previewKeyRef = useRef<string | null>(null);
    // The shown preview's object URL — revoked when replaced and on unmount.
    const previewUrlRef = useRef<string | null>(null);
    useEffect(
        () => () => {
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        },
        [],
    );
    const [isGenerating, setIsGenerating] = useState(false);

    // Map subset state (GEN-765): selection applies optimistically; the PATCH +
    // prefill re-resolution run debounced, guarded against stale responses.
    const [selectedBuildingIds, setSelectedBuildingIds] = useState<Set<string>>(new Set());
    // The latest selection, written right next to every setSelectedBuildingIds:
    // the toggle handler and persistSelection read it, so neither can work on a
    // subset from an earlier render (two toggles, or a toggle then a save, in
    // the same frame).
    const selectedIdsRef = useRef<Set<string>>(new Set());
    const [buildingData, setBuildingData] = useState<Building[]>([]);
    const [buildingAddressMap, setBuildingAddressMap] = useState<Map<string, string>>(new Map());
    const [isPrefillRefreshing, setIsPrefillRefreshing] = useState(false);
    // A map toggle still inside its debounce: the shown values belong to the
    // previous subset until it is PATCHed and re-prefilled, so the save waits.
    const [isTogglePending, setIsTogglePending] = useState(false);
    const prefillSeqRef = useRef(0);
    const patchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Request discipline of the map refresh. Every settled subset is PATCHed
    // at once (persisted state: the save re-resolves from it, ADR-011) — one
    // PATCH at a time. Only the expensive prefill (prod ~11 s; QA saw up to 4
    // concurrent ones for one Steckbrief) is coalesced: at most one in flight,
    // plus one follow-up when the persisted subset moved on meanwhile.
    // `refreshGenRef` counts page contexts (load, unmount): an older chain
    // never prefills into a newer one. The keys are selectionKeyOf values,
    // null while unknown: the newest subset PATCHed or queued, the newest one
    // the server confirmed, and the one the shown prefill resolved from.
    const refreshGenRef = useRef(0);
    const patchChainRef = useRef<SelectionPatchChain | null>(null);
    const prefillAbortRef = useRef<AbortController | null>(null);
    const requestedKeyRef = useRef<string | null>(null);
    const persistedKeyRef = useRef<string | null>(null);
    const prefilledKeyRef = useRef<string | null>(null);
    // Building ids already handed to the geocoder — see the geocoding effect below.
    const geocodeRequestedRef = useRef<Set<string>>(new Set());

    // Gebäudefoto map capture (GEN-778): the latest processed capture for the
    // form (versioned — the form adopts each new one) and the building
    // selection it was taken with, to flag a photo that no longer matches.
    const [capturedPhoto, setCapturedPhoto] = useState<{ dataUri: string; version: number } | null>(null);
    const [photoSelectionKey, setPhotoSelectionKey] = useState<string | null>(null);

    // Back target: the REA page with this REA and its Steckbriefe tab selected.
    const backUrl = reaPageUrl(reaId ?? '', 'steckbriefe');

    useEffect(() => {
        if (!steckbriefId || !reaId) return;
        let cancelled = false;
        // Every load of this context shares one controller, aborted in the
        // cleanup: a reload or a route switch must not leave an 11-52 s prefill
        // running server-side for a page nobody looks at any more.
        const controller = new AbortController();
        const { signal } = controller;
        // The previous context's map refresh: its unsettled toggle and its
        // prefill are dropped; its PATCHes run on (see settleSelection).
        refreshGenRef.current += 1;
        if (patchTimerRef.current !== null) clearTimeout(patchTimerRef.current);
        patchTimerRef.current = null;
        setIsTogglePending(false);
        prefillAbortRef.current?.abort();
        prefillAbortRef.current = null;
        requestedKeyRef.current = null;
        persistedKeyRef.current = null;
        prefilledKeyRef.current = null;
        prefillSeqRef.current += 1;
        setIsPrefillRefreshing(false);
        // A PATCH of the previous context on THIS Steckbrief (variant route
        // switch) may still be running: read the subset and prefill only once
        // it landed, or both would show the subset before it — and the seeded
        // keys would then suppress the PATCH that corrects it.
        const previousChain = patchChainRef.current;
        const patchesLanded =
            previousChain !== null && previousChain.steckbriefId === steckbriefId
                ? previousChain.idle
                : Promise.resolve();
        setSteckbrief(null);
        setRea(null);
        setPrefill(null);
        setOpenedVariant(null);
        setActiveVariantNames([]);
        setLoadError(null);
        // A capture belongs to the variant it was taken for (GEN-778): both
        // routes share this page element, so switching Steckbrief/variant keeps
        // the page mounted — the form remounts for the new context and must not
        // adopt the previous context's photo or show its retake hint.
        setCapturedPhoto(null);
        setPhotoSelectionKey(null);
        const fail = (err: unknown, fallback: string) => {
            if (cancelled || isAborted(err)) return;
            setLoadError(isProblemDetailTextOr(err, fallback));
        };
        void patchesLanded.then(() => {
            if (cancelled) return;
            ensanSteckbriefService
                .getSteckbrief(steckbriefId, { signal })
                .then((data) => {
                    if (cancelled) return;
                    setSteckbrief(data);
                    const loaded = new Set(data.buildingIds);
                    selectedIdsRef.current = loaded;
                    setSelectedBuildingIds(loaded);
                    // The initial prefill (create mode) resolves from this
                    // same persisted subset; open mode's stamp counts as it.
                    const key = selectionKeyOf(data.buildingIds);
                    requestedKeyRef.current = key;
                    persistedKeyRef.current = key;
                    prefilledKeyRef.current = key;
                })
                .catch((err) => fail(err, t('Failed to load the Steckbrief.')));
        });
        // The REA in parallel: the map picker needs its buildings and the
        // "(i/n) function — address" labels are built like on the REA page.
        admiRealEstateAssetService
            .getRealEstateAsset(reaId, signal)
            .then((data) => {
                if (!cancelled) setRea(data);
            })
            .catch((err) => fail(err, t('Failed to load the real estate asset.')));
        // Active Variant names for the inline uniqueness check (GEN-763).
        ensanSteckbriefService
            .getVariants(steckbriefId, { signal })
            .then((variants) => {
                if (!cancelled) setActiveVariantNames(variants.map((variant) => variant.name));
            })
            .catch((err) => fail(err, t('Failed to load the variants.')));
        if (variantId) {
            // Open mode (GEN-765): the prefill is the Variant's own stamp — no
            // server prefill call, the stamp already IS the resolved state.
            ensanSteckbriefService
                .getVariant(steckbriefId, variantId, { signal })
                .then((variant) => {
                    if (cancelled) return;
                    setOpenedVariant(variant);
                    setPrefill(prefillFromStamp(variant));
                })
                .catch((err) => fail(err, t('Failed to load the variant.')));
        } else {
            // Prefill immediately on route entry (GEN-701 QA feedback: no pre-step
            // page). The prefill request must name a kind because the API echoes it
            // back, but its RESOLUTION is kind-independent — the backend
            // SteckbriefVariantService.prefill uses request.kind only for that echo,
            // so the resolved fields are identical for SALES and FINAL. One SALES
            // prefill therefore serves both kinds, and flipping the kind switch in
            // the form does NOT re-prefill: the switch only drives validation and
            // the kind-conditional fields.
            void patchesLanded.then(() => {
                if (cancelled) return;
                ensanSteckbriefService
                    .prefillVariant(steckbriefId, 'SALES', { signal })
                    .then((data) => {
                        if (!cancelled) setPrefill(data);
                    })
                    .catch((err) => fail(err, t('Failed to prefill the variant form.')));
            });
        }
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [steckbriefId, reaId, variantId, t]);

    // The model defaults (GEN-802) — global configuration, fetched once per
    // page mount: a Steckbrief/variant switch keeps them. A failure never
    // blocks the page: the defaulted fields start empty and a warning asks for
    // the share by hand — required on FINAL since GEN-801, so a FINAL save
    // blocks until it is entered; SALES may leave it to the backend default.
    useEffect(() => {
        let cancelled = false;
        ensanSteckbriefService
            .getSteckbriefAssumptionDefaults()
            .then((data) => {
                if (!cancelled) setAssumptionDefaults(data);
            })
            .catch(() => {
                if (cancelled) return;
                setAssumptionDefaults(null);
                dispatch(
                    showSnackbar({
                        message: t(
                            'The model defaults could not be loaded — the window area share starts empty; please enter it by hand.',
                        ),
                        severity: NotificationSeverity.Warning,
                    }),
                );
            });
        return () => {
            cancelled = true;
        };
    }, [dispatch, t]);

    // Geocode building centers for the "(i/n) function — address" labels —
    // sequential like the REA page (Geocoder per-second limit), and through
    // `loader.importLibrary('geocoding')` so it never races the Maps script
    // load. Degrades silently: no Maps API → labels stay "(i/n) function".
    useEffect(() => {
        // Deliberately keyed on buildingData only: reading the address map here
        // would make the effect depend on its own output — every resolved address
        // then cancels the in-flight loop and restarts a fresh Geocoder for the
        // remainder. The ref tracks what was already requested instead (a failed
        // geocode is not retried; the label degrades to "(i/n) function").
        const toGeocode = buildingData.filter((building) => !geocodeRequestedRef.current.has(building.id));
        if (toGeocode.length === 0) return;
        toGeocode.forEach((building) => geocodeRequestedRef.current.add(building.id));
        let cancelled = false;
        (async () => {
            try {
                const { Geocoder } = (await googleMapsLoader.importLibrary(
                    'geocoding',
                )) as google.maps.GeocodingLibrary;
                if (cancelled) return;
                const geocoder = new Geocoder();
                for (const building of toGeocode) {
                    if (cancelled) return;
                    const center = buildingCenter(building);
                    if (!center) continue;
                    const response = await geocoder.geocode({ location: center }).catch(() => undefined);
                    if (cancelled) return;
                    const address = response?.results[0]?.formatted_address;
                    if (address) {
                        setBuildingAddressMap((prev) => new Map(prev).set(building.id, address));
                    }
                }
            } catch {
                // Loader failure (network, invalid API key, jsdom) — keep the
                // address-less labels; the picker still works.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [buildingData, googleMapsLoader]);

    /** "(i/n) function — address" — position in the REA like the REA page's option labels. */
    const buildingLabel = useCallback(
        (buildingId: string): string => {
            const reaBuildings = rea?.buildings ?? [];
            const index = reaBuildings.findIndex((building) => building.id === buildingId);
            const position = index >= 0 ? `(${index + 1}/${reaBuildings.length})` : null;
            const functionName = buildingData.find((building) => building.id === buildingId)?.buildingFunction?.name;
            const address = buildingAddressMap.get(buildingId);
            const detail = [functionName, address ? stripCountry(address) : null].filter(Boolean).join(' — ');
            const label = [position, detail].filter(Boolean).join(' ');
            return label || buildingId.slice(0, 8);
        },
        [rea, buildingData, buildingAddressMap],
    );

    /** Map refresh in flight or queued: a PATCH of this context, or its prefill. */
    function syncPrefillRefreshing() {
        const chain = patchChainRef.current;
        const patching = chain !== null && chain.gen === refreshGenRef.current;
        setIsPrefillRefreshing(patching || prefillAbortRef.current !== null);
    }

    /**
     * A debounce-settled subset. Equal to the newest subset PATCHed or queued
     * → nothing (a toggle and its toggle-back within the debounce cost
     * nothing). Otherwise it is PATCHed at once — or, while a PATCH of this
     * context runs, queued as the newest one (it replaces an older queued
     * subset). A PATCH is never aborted and a queued one still goes out after
     * leaving the page: the change "persists immediately"
     * (updateSteckbrief), and aborting client-side would leave the persisted
     * subset undetermined, as the server may apply it anyway.
     */
    function settleSelection(ids: Set<string>) {
        if (!steckbriefId) return;
        const key = selectionKeyOf(ids);
        if (key === requestedKeyRef.current) return;
        requestedKeyRef.current = key;
        const chain = patchChainRef.current;
        if (chain !== null && chain.gen === refreshGenRef.current) {
            chain.queued = ids;
            return;
        }
        startPatchChain(steckbriefId, ids);
    }

    function startPatchChain(id: string, first: Set<string>) {
        const gen = refreshGenRef.current;
        const chain: SelectionPatchChain = { steckbriefId: id, gen, queued: null, idle: Promise.resolve() };
        chain.idle = (async () => {
            let ids: Set<string> | null = first;
            let landedKey: string | null = null;
            while (ids !== null) {
                const key = selectionKeyOf(ids);
                try {
                    await ensanSteckbriefService.updateSteckbrief(id, { buildingIds: [...ids] });
                    landedKey = key;
                    if (gen === refreshGenRef.current) persistedKeyRef.current = key;
                } catch (err) {
                    if (gen === refreshGenRef.current) {
                        // Unknown now: the same subset settled again is retried.
                        requestedKeyRef.current = null;
                        reportRefreshFailure(err);
                    }
                }
                ids = chain.queued;
                chain.queued = null;
                if (ids !== null && selectionKeyOf(ids) === landedKey) ids = null;
            }
        })();
        patchChainRef.current = chain;
        syncPrefillRefreshing();
        void chain.idle.then(() => {
            if (patchChainRef.current === chain) patchChainRef.current = null;
            if (gen !== refreshGenRef.current) return;
            requestPrefill(id);
            syncPrefillRefreshing();
        });
    }

    /**
     * Re-resolve the prefill once the PATCHes landed — unless one is running
     * (its settle decides on the follow-up), a PATCH is still under way (the
     * last one asks again), or the persisted subset is the one the shown
     * prefill resolved from (e.g. off and on again in two settled steps).
     */
    function requestPrefill(id: string) {
        if (prefillAbortRef.current !== null) return;
        const chain = patchChainRef.current;
        if (chain !== null && chain.gen === refreshGenRef.current) return;
        const persisted = persistedKeyRef.current;
        if (persisted === null || persisted === prefilledKeyRef.current) return;
        void runPrefill(id, persisted);
    }

    /**
     * The prefill of the persisted subset `key`. The form merges the result
     * keyed on `prefillVersion` (USER_PROVIDED edits win). Stale-guarded: only
     * the newest refresh may apply. Aborted on unmount / context switch; when
     * it succeeds, a subset persisted meanwhile gets exactly ONE follow-up.
     */
    async function runPrefill(id: string, key: string) {
        const controller = new AbortController();
        prefillAbortRef.current = controller;
        const seq = ++prefillSeqRef.current;
        let succeeded = false;
        syncPrefillRefreshing();
        try {
            const refreshed = await ensanSteckbriefService.prefillVariant(id, 'SALES', { signal: controller.signal });
            if (seq !== prefillSeqRef.current) return;
            prefilledKeyRef.current = key;
            succeeded = true;
            setPrefill(refreshed);
            setPrefillVersion((version) => version + 1);
        } catch (err) {
            if (isAborted(err) || seq !== prefillSeqRef.current) return;
            // Unknown now: the same subset settled again re-PATCHes and re-prefills.
            prefilledKeyRef.current = null;
            requestedKeyRef.current = null;
            reportRefreshFailure(err);
        } finally {
            // An aborted prefill was reset by whoever aborted it (unmount, context switch).
            if (!controller.signal.aborted && seq === prefillSeqRef.current) {
                prefillAbortRef.current = null;
                if (succeeded) requestPrefill(id);
                syncPrefillRefreshing();
            }
        }
    }

    function reportRefreshFailure(err: unknown) {
        dispatch(
            showSnackbar({
                message: isProblemDetailTextOr(err, t('Failed to update the building selection.')),
                severity: NotificationSeverity.Error,
            }),
        );
    }

    /**
     * Before a save: settle a toggle still inside its debounce and wait for
     * the PATCHes — the save re-resolves the catalog values server-side from
     * the PERSISTED subset (ADR-011), which must be the one the map shows.
     * Defensive: the form already blocks the save while a toggle is pending
     * or its refresh runs (`prefillLoading`), so this normally finds nothing.
     */
    async function persistSelection() {
        if (patchTimerRef.current !== null) {
            clearTimeout(patchTimerRef.current);
            patchTimerRef.current = null;
            setIsTogglePending(false);
            settleSelection(selectedIdsRef.current);
        }
        const chain = patchChainRef.current;
        if (chain !== null && chain.gen === refreshGenRef.current) await chain.idle;
    }

    function handleToggleBuilding(buildingId: string) {
        const next = new Set(selectedIdsRef.current);
        if (next.has(buildingId)) {
            // A Steckbrief must keep covering at least one building — the
            // backend rejects an empty subset, and an empty prefill is
            // meaningless. Explained by the tooltip on the picker.
            if (next.size === 1) return;
            next.delete(buildingId);
        } else {
            next.add(buildingId);
        }
        selectedIdsRef.current = next;
        setSelectedBuildingIds(next);
        if (patchTimerRef.current !== null) clearTimeout(patchTimerRef.current);
        setIsTogglePending(true);
        patchTimerRef.current = setTimeout(() => {
            patchTimerRef.current = null;
            setIsTogglePending(false);
            settleSelection(next);
        }, MAP_PATCH_DEBOUNCE_MS);
    }
    // Leaving the page: an unsettled toggle is dropped and a running prefill
    // refresh stopped; PATCHes already settled still land (settleSelection).
    useEffect(
        () => () => {
            refreshGenRef.current += 1;
            if (patchTimerRef.current !== null) clearTimeout(patchTimerRef.current);
            patchTimerRef.current = null;
            prefillAbortRef.current?.abort();
            prefillAbortRef.current = null;
        },
        [],
    );

    const handlePrefillMerged = useCallback(
        (changedFieldCount: number) => {
            dispatch(
                showSnackbar({
                    message: t('Prefill updated ({{count}} fields)', { count: changedFieldCount }),
                    severity: NotificationSeverity.Success,
                }),
            );
        },
        [dispatch, t],
    );

    async function handleSave(request: CreateVariantRequest): Promise<VariantSaveResult> {
        if (!steckbrief) return 'failed';
        setIsSaving(true);
        try {
            await persistSelection();
            await ensanSteckbriefService.createVariant(steckbrief.id, request);
            dispatch(showSnackbar({ message: t('Variant saved.'), severity: NotificationSeverity.Success }));
            navigate(backUrl);
            return 'saved';
        } catch (err) {
            // GEN-763: a save racing past the inline check — mapped back onto
            // the inline name error by the form.
            if (isProblemDetailCode(err, 'VARIANT_NAME_TAKEN')) return 'nameTaken';
            dispatch(
                showSnackbar({
                    message: isProblemDetailTextOr(err, t('Failed to save the variant.')),
                    severity: NotificationSeverity.Error,
                }),
            );
            return 'failed';
        } finally {
            setIsSaving(false);
        }
    }

    /**
     * The success notice of a generation — in warning colour with the warning-level assumptions of
     * the replay appended (GEN-700 D4: a stale lookalike reference generates anyway, but says so).
     */
    function generatedNotice(document: VariantDocumentDto) {
        const message = t('Document generated: {{url}}', { url: document.documentUrl });
        const warnings = document.warnings ?? [];
        if (warnings.length === 0) return { message, severity: NotificationSeverity.Success };
        const details = warnings.map((warning) => assumptionMessage(t, i18n.language, warning)).join(' ');
        return { message: `${message} — ${details}`, severity: NotificationSeverity.Warning };
    }

    /** "Speichern & PDF erzeugen": save, then generate; back to the tab either way once saved. */
    async function handleSaveAndGenerate(request: CreateVariantRequest): Promise<VariantSaveResult> {
        if (!steckbrief) return 'failed';
        setIsSaving(true);
        try {
            await persistSelection();
            const variant = await ensanSteckbriefService.createVariant(steckbrief.id, request);
            try {
                const document = await ensanSteckbriefService.generateVariantDocument(steckbrief.id, variant.id);
                dispatch(showSnackbar(generatedNotice(document)));
            } catch (genErr) {
                // The Variant IS saved — report the generation failure and
                // return to the tab, where generation can be retried.
                dispatch(
                    showSnackbar({
                        message: isProblemDetailTextOr(genErr, t('Variant saved, but the document generation failed.')),
                        severity: NotificationSeverity.Error,
                    }),
                );
            }
            navigate(backUrl);
            return 'saved';
        } catch (err) {
            if (isProblemDetailCode(err, 'VARIANT_NAME_TAKEN')) return 'nameTaken';
            dispatch(
                showSnackbar({
                    message: isProblemDetailTextOr(err, t('Failed to save the variant.')),
                    severity: NotificationSeverity.Error,
                }),
            );
            return 'failed';
        } finally {
            setIsSaving(false);
        }
    }

    /**
     * Inline "Steckbrief-Vorschau" (GEN-762): stateless PDF render → the form's
     * preview panel. The form asks after every successful calculate and on a
     * kind switch, but each render re-runs the whole calculate pipeline
     * server-side plus the PDF render (QA mean 2.0 s, body up to 198 KB): a
     * payload identical to the newest render — in flight or shown — is
     * skipped; any change (values, measures, kind, PV estimate) re-renders.
     */
    async function handlePreview(request: PreviewSteckbriefDocumentRequest) {
        if (!steckbrief) return;
        const key = JSON.stringify({ steckbriefId: steckbrief.id, selection: selectionKey, request });
        if (key === previewKeyRef.current) return;
        previewKeyRef.current = key;
        const seq = ++previewSeqRef.current;
        setIsPreviewing(true);
        try {
            const next = await ensanSteckbriefService.previewSteckbriefDocument(steckbrief.id, request);
            if (seq !== previewSeqRef.current) return;
            // Force the PDF type so the iframe renders instead of downloading.
            const url = URL.createObjectURL(new Blob([next.blob], { type: 'application/pdf' }));
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = url;
            setPreview({ ...next, url });
            setPreviewError(null);
        } catch (err) {
            if (seq !== previewSeqRef.current) return;
            // A failed render is retried by the next identical request.
            previewKeyRef.current = null;
            setPreviewError(isProblemDetailTextOr(err, t('Failed to load the preview.')));
        } finally {
            if (seq === previewSeqRef.current) setIsPreviewing(false);
        }
    }

    /**
     * Open mode "PDF erzeugen" (GEN-765): generates from the OPENED Variant's
     * SAVED stamp — local edits are deliberately not included (the footer
     * button's tooltip says exactly that). 409 handling mirrors the tab.
     */
    async function handleGenerateOpened() {
        if (!steckbrief || !openedVariant) return;
        setIsGenerating(true);
        try {
            const document = await ensanSteckbriefService.generateVariantDocument(steckbrief.id, openedVariant.id);
            setOpenedVariant({ ...openedVariant, documentInstanceId: document.documentInstanceId });
            dispatch(showSnackbar(generatedNotice(document)));
        } catch (err) {
            if (isProblemDetailCode(err, 'VARIANT_ALREADY_GENERATED')) {
                dispatch(
                    showSnackbar({
                        message: t(
                            'This variant already has a generated document — save a new variant for an updated print.',
                        ),
                        severity: NotificationSeverity.Error,
                    }),
                );
                // The document exists server-side — refetch so the action disappears.
                if (steckbriefId && variantId) {
                    ensanSteckbriefService
                        .getVariant(steckbriefId, variantId)
                        .then(setOpenedVariant)
                        .catch(() => undefined);
                }
            } else {
                dispatch(
                    showSnackbar({
                        message: isProblemDetailTextOr(err, t('Failed to generate the document.')),
                        severity: NotificationSeverity.Error,
                    }),
                );
            }
        } finally {
            setIsGenerating(false);
        }
    }

    // Open-mode measures seeding from the stamp (GEN-765): overrides only when
    // the stamp's `overrides` provenance carries the respective key — an
    // engine-followed value must stay engine-followed in the reopened form.
    const stampMeasures = openedVariant?.dataStamp.measures ?? null;
    const initialSelectedMeasures = stampMeasures ? (stampMeasures.selected as MeasureKey[]) : null;
    const initialHeatingOverride =
        stampMeasures && HEATING_SYSTEM_OVERRIDE_KEY in stampMeasures.overrides
            ? ((stampMeasures.heatingSystem?.selected ?? null) as RecommendedHeatingSystemKey | null)
            : null;
    const initialPriorityOverride =
        stampMeasures && PRIORITIES_OVERRIDE_KEY in stampMeasures.overrides
            ? ((stampMeasures.priorities ?? null) as MeasureKey[] | null)
            : null;
    const initialMeasureReasons = (stampMeasures?.reasons ?? {}) as Partial<Record<MeasureKey, string>>;
    // GEN-799: the opened Variant's stamped PV estimate is what the form
    // displays first — its id travels on the initial /calculate (absent on a
    // pre-feature stamp → null, like create mode).
    const initialPvEstimateId = openedVariant?.dataStamp.pvEstimate?.estimateId ?? null;

    // Two gates (prod 2026-09-30: the REA arrived at +0.45 s, the prefill at
    // +11 s): the map needs only the Steckbrief and the REA, so its building
    // reads and the geocoding start at once; the form waits for the prefill
    // and the model defaults. Until then the map is read-only — a toggle or a
    // capture before the initial prefill returned would race it.
    const pickerReady = steckbrief !== null && rea !== null;
    const formReady = pickerReady && prefill !== null && assumptionDefaults !== undefined;
    const selectedCount = selectedBuildingIds.size;
    const selectionKey = selectionKeyOf(selectedBuildingIds);

    /**
     * "Save map image" (GEN-778): the captured map canvas → the print format
     * (259.8:166 crop, rounded-corner + diagonal mask, JPEG) → the form's photo
     * field. Returns the processed photo for the button's preview modal.
     */
    function handleMapCapture(canvas: HTMLCanvasElement): string | null {
        try {
            const dataUri = processCapturedPhoto(canvas);
            setCapturedPhoto((previous) => ({ dataUri, version: (previous?.version ?? 0) + 1 }));
            setPhotoSelectionKey(selectionKey);
            return dataUri;
        } catch (err) {
            dispatch(
                showSnackbar({
                    message:
                        err instanceof PhotoTooLargeError
                            ? t('The map image is too large ({{kb}} KB, max. {{maxKb}} KB). Please zoom in.', {
                                  kb: Math.round(err.bytes / 1024),
                                  maxKb: Math.round(STECKBRIEF_PHOTO_MAX_BYTES / 1024),
                              })
                            : t('The map image could not be processed.'),
                    severity: NotificationSeverity.Error,
                }),
            );
            return null;
        }
    }

    return (
        <Stack spacing={2} sx={{ p: 2, maxWidth: 960 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <IconButton onClick={() => navigate(backUrl)} aria-label={t('Back to the real estate asset')}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5">
                    {steckbrief && openedVariant
                        ? t('Variant "{{variant}}" of {{name}} ({{code}})', {
                              variant: openedVariant.name,
                              name: steckbrief.name,
                              code: steckbrief.code,
                          })
                        : steckbrief
                          ? t('New variant for {{name}} ({{code}})', {
                                name: steckbrief.name,
                                code: steckbrief.code,
                            })
                          : variantId
                            ? t('Variant')
                            : t('New variant')}
                </Typography>
            </Stack>

            {loadError ? (
                <Alert severity="error">{loadError}</Alert>
            ) : !pickerReady ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <>
                    {/* Building subset ON TOP of the form (GEN-765): toggling
                        persists immediately (debounced PATCH) and re-prefills. */}
                    <Box data-testid="variant-form-map">
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            {t('Buildings covered by this Steckbrief')}
                        </Typography>
                        <SteckbriefBuildingMapPicker
                            buildings={rea.buildings}
                            buildingLabel={buildingLabel}
                            selectedIds={selectedBuildingIds}
                            onToggle={handleToggleBuilding}
                            disabled={isSaving || !formReady}
                            onBuildingDataLoaded={setBuildingData}
                            selectionTooltip={
                                selectedCount <= 1 ? t('At least one building must remain selected.') : undefined
                            }
                            snapshot={{
                                onCapture: handleMapCapture,
                                disabled: isSaving || !formReady,
                                hint:
                                    photoSelectionKey !== null && photoSelectionKey !== selectionKey ? (
                                        <Typography variant="caption" sx={{ color: 'warning.main' }}>
                                            {t(
                                                'The building selection changed since the photo was taken — save the map image again.',
                                            )}
                                        </Typography>
                                    ) : null,
                            }}
                        />
                        {isPrefillRefreshing && <LinearProgress sx={{ mt: 0.5 }} />}
                    </Box>

                    {!formReady ? (
                        <Box
                            sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
                            data-testid="variant-form-loading"
                        >
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <SteckbriefVariantForm
                            steckbrief={steckbrief}
                            prefill={prefill}
                            prefillVersion={prefillVersion}
                            assumptionDefaults={assumptionDefaults}
                            // GEN-801: always FINAL first (its required rules apply) — also
                            // when an opened SALES Variant seeds the form; the switch stays.
                            initialSelectedMeasures={initialSelectedMeasures}
                            initialHeatingOverride={initialHeatingOverride}
                            initialPriorityOverride={initialPriorityOverride}
                            initialMeasureReasons={initialMeasureReasons}
                            initialPvEstimateId={initialPvEstimateId}
                            openedVariant={openedVariant}
                            activeVariantNames={activeVariantNames}
                            saving={isSaving}
                            previewing={isPreviewing}
                            generating={isGenerating}
                            onSave={handleSave}
                            onSaveAndGenerate={handleSaveAndGenerate}
                            onPreview={(request) => void handlePreview(request)}
                            preview={preview}
                            previewError={previewError}
                            previewFileName={`${steckbrief.code}-preview.pdf`}
                            onGenerateOpened={openedVariant ? () => void handleGenerateOpened() : undefined}
                            onPrefillMerged={handlePrefillMerged}
                            capturedPhoto={capturedPhoto}
                            // Also blocks the save-shaped actions: until the refresh merged,
                            // the form shows values of the previous subset while the server
                            // re-resolves the save from the new one (ADR-011 stamp diff).
                            prefillLoading={isPrefillRefreshing || isTogglePending}
                            onCancel={() => navigate(backUrl)}
                        />
                    )}
                </>
            )}
        </Stack>
    );
}
